import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import styles from './PreCaseForm.module.scss';
import RadioButtonGroup from "../../../../components/radioButtonGroup/RadioButtonGroup";
import Dropdown from "../../../../components/dropdown/Dropdown";
import CustomDatePicker from "../../../../components/customDatePicker/CustomDatePicker";
import TextAreaField from "../../../../components/textAreaField/TextAreaField";
import MultiSelectDropdown from "../../../../components/multiSelectDropdown/MultiSelectDropdown";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { useToasts } from "../../../../contexts/ToastContext";

const PreCaseForm = forwardRef(({
  formData,
  clinicalSitesList = [],
  instructorList = [],
  studentYearList = [],
  specialitiesList = [],
  surgicalServiceList = [],
  patientPhysicalStatusList = [],
  patientAgeList = [],
  ageRangesList = [],
  onSave,
  disabled = false
}, ref) => {
  const [clinicalSiteId, setClinicalSiteId] = useState(null);
  const [clinicalSiteIdError, setClinicalSiteIdError] = useState('');
  const [dateOfCase, setDateOfCase] = useState(null);
  const [dateOfCaseError, setDateOfCaseError] = useState('');
  const [instructorId, setInstructorId] = useState(null);
  const [instructorIdError, setInstructorIdError] = useState('');
  const [yearId, setYearId] = useState(null);
  const [specialityIds, setSpecialityIds] = useState([]);
  const [specialityIdsError, setSpecialityIdsError] = useState('');
  const [surgicalService, setSurgicalService] = useState([]);
  const [surgicalServiceError, setSurgicalServiceError] = useState('');
  const [patientPhysicalStatus, setPatientPhysicalStatus] = useState(null);
  const [patientPhysicalStatusError, setPatientPhysicalStatusError] = useState('');
  const [emergency, setEmergency] = useState(null);
  const [emergencyError, setEmergencyError] = useState('');
  const [patientAge, setPatientAge] = useState(null);
  const [patientAgeError, setPatientAgeError] = useState('');
  const [ageRange, setAgeRange] = useState('');
  const [ageRangeError, setAgeRangeError] = useState('');
  const [surgeryDescription, setSurgeryDescription] = useState('');
  const { showToast } = useToasts();

  useEffect(() => {
    setClinicalSiteId(formData?.clinicalSiteId || null);
    setDateOfCase(formData?.dateOfCase ? new Date(formData.dateOfCase) : null);
    setInstructorId(formData?.instructorId || null);
    setYearId(formData?.yearId || null);
    setSpecialityIds(formData?.specialityIds || []);
    setSurgicalService(formData?.surgicalService || []);
    setPatientPhysicalStatus(formData.patientPhysicalStatus || null);
    setEmergency(formData.emergency);
    setPatientAge(formData.patientAge || null);
    setAgeRange(formData.ageRange || null);
    setSurgeryDescription(formData.surgeryDescription || '');
  }, [formData]);

  const validateClinicalSiteId = () => {
    if(!clinicalSiteId) {
      return 'Clinical Site is required';
    }
    return '';
  }

  const validateDateOfCase = () => {
    if(!dateOfCase) {
      return 'Date of Case is required';
    }
    return '';
  }

  const validateInstructorId = () => {
    if(!instructorId) {
      return 'Instructor is required';
    }
    return '';
  }

  const validateSpecialityIds = () => {
    if(specialityIds.length === 0) {
      return 'Rotation is required';
    }
    return '';
  }

  const validateSurgicalService = () => {
    if(surgicalService.length === 0) {
      return 'Surgical Service is required';
    }
    return '';
  }

  const validatePatientPhysicalStatus = () => {
    if(!patientPhysicalStatus) {
      return 'Surgical Service is required';
    }
    return '';
  }

  const validateEmergency = () => {
    if(emergency === null) {
      return 'Emergency is required';
    }
    return '';
  }

  const validatePatientAge = () => {
    if(!patientAge) {
      return 'Patient’s Age is required';
    }
    return '';
  }

  const validateAgeRange = () => {
    if(!ageRange) {
      return 'Age Range is required';
    }
    return '';
  }

  const validateForm = () => {
    const clinicalSiteIdValidationResult = validateClinicalSiteId();
    const dateOfCaseValidationResult = validateDateOfCase();
    const instructorIdValidationResult = validateInstructorId();
    const specialityIdsValidationResult = validateSpecialityIds();
    const surgicalServiceValidationResult = validateSurgicalService();
    const patientPhysicalStatusValidationResult = validatePatientPhysicalStatus();
    const emergencyValidationResult = validateEmergency();
    const patientAgeValidationResult = validatePatientAge();
    const ageRangeValidationResult = validateAgeRange();

    setClinicalSiteIdError(clinicalSiteIdValidationResult);
    setDateOfCaseError(dateOfCaseValidationResult);
    setInstructorIdError(instructorIdValidationResult);
    setSpecialityIdsError(specialityIdsValidationResult);
    setSurgicalServiceError(surgicalServiceValidationResult);
    setPatientPhysicalStatusError(patientPhysicalStatusValidationResult);
    setEmergencyError(emergencyValidationResult);
    setPatientAgeError(patientAgeValidationResult);
    setAgeRangeError(ageRangeValidationResult);

    const errors = [
      clinicalSiteIdValidationResult,
      dateOfCaseValidationResult,
      instructorIdValidationResult,
      specialityIdsValidationResult,
      surgicalServiceValidationResult,
      patientPhysicalStatusValidationResult,
      emergencyValidationResult,
      patientAgeValidationResult,
      ageRangeValidationResult
    ];
  
    const errorCount = errors.filter(error => error !== '').length;
  
    return errorCount;
  };

  // Expose the function to the parent
  useImperativeHandle(ref, () => ({
    validateForm
  }));
  
  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <div className={styles.row}>
        <div className={styles.column50}>
          <Dropdown
            label="Clinical Site"
            options={clinicalSitesList}
            value={clinicalSiteId}
            onChange={(value) => {
              setClinicalSiteId(value);
              onSave({
                clinicalSiteId: value
              })
            }}
            error={clinicalSiteIdError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column20}>
          <CustomDatePicker
            label="Date of Case"
            date={dateOfCase}
            onChange={(date) => {
              setDateOfCase(date);
              onSave({
                dateOfCase: date ? format(date, "yyyy-MM-dd") : null
              })
            }}
            showIcon={true}
            maxDate={new Date()}
            error={dateOfCaseError}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column40}>
          <Dropdown
            label="Choose your Instructor"
            options={instructorList}
            value={instructorId}
            onChange={(value) => {
              setInstructorId(value)
              onSave({
                instructorId: value
              })
            }}
            error={instructorIdError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column20}>
          <Dropdown
            label="Year"
            options={studentYearList}
            value={yearId}
            onChange={(value) => {
              setYearId(value);
            }}
            disabled={true}
          />
        </div>
        <div className={styles.column40}>
          <MultiSelectDropdown
            label="Rotation"
            options={specialitiesList}
            selected={specialityIds}
            onChange={(value) => {
              if(value.length > 3) {
                showToast('You can select up to 3 rotations only.', 'error')
              } else {
                setSpecialityIds(value)
                onSave({
                  specialityIds: value
                });
              }
            }}
            error={specialityIdsError}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column50}>
          <MultiSelectDropdown
            label="Surgical Service"
            options={surgicalServiceList}
            selected={surgicalService}
            onChange={(value) => {
              if(value.length > 3) {
                showToast('You can select up to 3 surgical services only.', 'error')
              } else {
                setSurgicalService(value)
                onSave({
                  surgicalService: value
                });
              }
            }}
            error={surgicalServiceError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column30}>
          <Dropdown
            label="Patient’s Physical Status"
            options={patientPhysicalStatusList}
            value={patientPhysicalStatus}
            onChange={(value) => {
              setPatientPhysicalStatus(value)
              onSave({
                patientPhysicalStatus: value
              });
            }}
            error={patientPhysicalStatusError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column20}>
          <RadioButtonGroup
            label="Emergency"
            options={[
              {
                label: 'Yes',
                value: true
              },
              {
                label: 'No',
                value: false
              }
            ]}
            selected={emergency}
            onChange={(value) => {
              setEmergency(value)
              onSave({
                emergency: value
              })
            }}
            error={emergencyError}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column20}>
          <Dropdown
            label="Patient’s Age"
            options={patientAgeList}
            value={patientAge}
            onChange={(value) => {
              setPatientAge(value);
              onSave(typeof value === "string" ? {
                patientAge: value,
                patientAgeMonth: ""
              } :{
                patientAge: value.year,
                patientAgeMonth: value.month
              })
            }}
            searchable={true}
            error={patientAgeError}
            disabled={disabled}
          />
        </div>
        <div className={styles.column20}>
          <Dropdown
            label="Age Range"
            options={ageRangesList}
            value={ageRange}
            onChange={(value) => {
              setAgeRange(value);
              onSave({
                ageRange: value
              })
            }}
            error={ageRangeError}
            disabled={disabled}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.column80}>
          <TextAreaField
            label="Surgery Description"
            value={surgeryDescription}
            onChange={(value) => setSurgeryDescription(value)}
            onBlur={() => onSave({
              surgeryDescription: surgeryDescription
            })}
            limit={50}
            disabled={disabled}
          />
        </div>
      </div>
    </form>
  );
});

PreCaseForm.displayName = "PreCaseForm";

PreCaseForm.propTypes = {
  formData: PropTypes.object,
  clinicalSitesList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  instructorList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  studentYearList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  specialitiesList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  surgicalServiceList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  patientPhysicalStatusList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
    })
  ).isRequired,
  patientAgeList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.string.isRequired,
      subOptions: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.any.isRequired,
          label: PropTypes.string.isRequired,
        })
      )
    })
  ).isRequired,
  ageRangesList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  onSave: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

export default PreCaseForm;